// EntityTimesheetDetail.jsx

'use client';
import React, {
  useCallback, useState, useRef, useReducer, useEffect, useMemo,
} from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { throttle } from 'lodash';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TableCell,
  styled,
  ThemeProvider,
  createTheme,
  Grid,
  Tooltip,
} from '@mui/material';
import Filters from '../../Components/Filters.jsx';
import Statistics from '../../Components/Statistics.jsx';
import TimesheetTable from '../../Components/TimesheetTable.jsx';
import DayDetailDialog from '../../Components/DayDetailDialog.jsx';
import StatusFilterModal from '../../Components/StatusFilterModal.jsx';
import { useTimesheetDetail } from '../../hooks/useTimesheetDetail.js';
import { cellInfo, dayBg, fmtTime } from '../../utils/timesheetUtils.js';
import { format, isToday } from 'date-fns';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import * as XLSX from 'xlsx-js-style';
import Layout from '../../Components/Layout/Layout.jsx';

// Modern theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#ff4081' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#666666' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600, fontSize: '0.94rem' },
    body2: { fontSize: '0.64rem', color: '#666666' },
    caption: { fontSize: '0.6rem', color: '#999999' },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 7.5,
          boxShadow: '0 1.5px 6px rgba(0,0,0,0.1)',
          padding: '6px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4.5,
          textTransform: 'none',
          fontSize: '0.68rem',
          padding: '3px 9px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 7.5,
          boxShadow: '0 0.75px 4.5px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.65rem',
          backgroundColor: 'rgba(33, 33, 33, 0.9)',
          color: '#ffffff',
          borderRadius: 4,
          padding: '6px 8px',
          maxWidth: 300,
        },
        arrow: {
          color: 'rgba(33, 33, 33, 0.9)',
        },
      },
    },
  },
});

// Styled components for legend
const LegendBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.125),
  alignItems: 'center',
  backgroundColor: '#fafafa',
  padding: theme.spacing(0.75),
  border: '1px solid #e0e0e0',
  borderRadius: 4.5,
  boxShadow: '0 0.75px 3px rgba(0,0,0,0.05)',
}));

const LegendItemBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.375),
}));

const LegendSwatchBox = styled(Box)(({ color }) => ({
  width: 9,
  height: 9,
  backgroundColor: color,
  border: '1px solid #ccc',
  borderRadius: 2.25,
}));

const reqReducer = (s) => s + 1;

// Helper: tooltip text (учитываем подтверждение и локацию)
const generateTooltipText = (member, date, inf, vac, hasIn, dayObj) => {
  const parts = [];

  // 1) vacation
  if (vac) {
    parts.push(`Vacation: ${vac.reason}`);
  }

  // 2) issues / hours
  const issues = [];
  if (inf.late) issues.push('Late clock-in');
  if (inf.outOfZone) issues.push('Out of zone');
  if (inf.missedOut) issues.push('Missing clock-out');
  if (inf.earlyLeave) issues.push('Early clock-out');

  if (issues.length > 0) {
    parts.push(issues.join(' • '));
  } else if (inf.hrsFormatted) {
    parts.push(`Hours: ${inf.hrsFormatted}`);
  } else if (!vac) {
    parts.push('No records');
  }

  // 3) first clock-in details (time, lat/lon, approval)
  const firstIn = dayObj.records
    .filter((r) => r.event_type === 'clock_in')
    .sort((a, b) => a.timestamp - b.timestamp)[0];

  if (firstIn) {
    const timeStr = fmtTime(firstIn.timestamp);
    const lat = firstIn.lat ?? firstIn.latitude;
    const lon = firstIn.lon ?? firstIn.lng ?? firstIn.longitude;
    const hasLatLon = (typeof lat === 'number' && typeof lon === 'number');

    // manager confirmation
    let confLabel = 'Pending approval';
    if (firstIn.manager_confirmed === true) confLabel = 'Approved';
    if (firstIn.manager_confirmed === false) confLabel = 'Rejected';

    parts.push(`Clock-in: ${timeStr}`);
    if (hasLatLon) parts.push(`Location: ${lat.toFixed(5)}, ${lon.toFixed(5)}`);
    parts.push(`Status: ${confLabel}`);
  }

  return parts.join('\n');
};

// Vacation bg/border
const getVacationBackground = (vac, hasIn) => {
  if (vac && hasIn) return '#BBDEFB';
  if (vac) return '#BBDEFB';
  return null;
};
const getVacationBorder = (vac, hasIn) => {
  if (vac && hasIn) return '2px solid #4CAF50';
  return '1px solid rgba(224,224,224,0.8)';
};

export default function EntityTimesheetDetail() {
  const { entity_type, entity_id } = useParams();
  const navigate = useNavigate();

  const {
    filterStart, setFilterStart,
    filterEnd, setFilterEnd,
    dateRange, data,
    loading, err, setErr,
    detail, setDetail,
    fetchData,
  } = useTimesheetDetail(entity_type, entity_id);

  const [search, setSearch] = useState('');
  const [departments, setDepartments] = useState([]);
  const [teamsApi, setTeamsApi] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [statusFilterModal, setStatusFilterModal] = useState({ open: false, date: null });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [teamsError, setTeamsError] = useState(null);

  // сортировка
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'department'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const [isSorting, setIsSorting] = useState(false); // анимация сортировки

  // Обработчик клика по заголовку
  const handleSort = useCallback((field) => {
    setIsSorting(true);
    if (sortBy === field) {
      requestAnimationFrame(() => {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      });
    } else {
      requestAnimationFrame(() => {
        setSortBy(field);
        setSortOrder('asc');
      });
    }
  }, [sortBy]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredDataRef = useRef(null);

  const tableWrapRef = useRef(null);
  const rowOverlayRef = useRef(null);
  const colOverlayRef = useRef(null);

  /* Telegram WebApp theme */
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.onEvent('themeChanged', () => {
        document.body.style.backgroundColor = window.Telegram.WebApp.themeParams.bg_color;
      });
      window.Telegram.WebApp.expand();
      document.documentElement.style.setProperty(
        '--tg-viewport-height',
        `${window.Telegram.WebApp.viewportHeight}px`,
      );
      window.Telegram.WebApp.ready();
    }
  }, []);

  /* Fetch departments / teams */
  useEffect(() => {
    const token = encodeURIComponent(localStorage.getItem('auth_token') || '');
    const API = process.env.REACT_APP_API_URL;

    const fetchJson = async (url) => {
      try {
        const res = await fetch(url);
        const j = await res.json();
        if (j?.status === 'success' || j?.success === true) return j;
        console.error(`Error fetching ${url}:`, j?.message || j);
        return null;
      } catch (e) {
        console.error(`Error fetching ${url}:`, e);
        return null;
      }
    };

    const load = async () => {
      // Departments
      const depData = await fetchJson(`${API}/admin/departments?auth_token=${token}`);
      const deps = depData?.departments || [];
      const sortedDeps = deps.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setDepartments(sortedDeps);
      if (sortedDeps.length === 1) setSelectedDepartment(sortedDeps[0]);

      // Teams
      let allTeams = [];
      if (sortedDeps.length > 0) {
        const ids = sortedDeps.map(d => d.id).filter(Boolean);
        const bulkUrl = `${API}/admin/teams?auth_token=${token}&department_ids=${ids.join(',')}`;
        const teamBulk = await fetchJson(bulkUrl);
        if (teamBulk?.teams?.length) {
          allTeams = teamBulk.teams;
        } else {
          const perDep = await Promise.all(
            ids.map(id => fetchJson(`${API}/admin/teams?auth_token=${token}&department_id=${id}`))
          );
          allTeams = perDep.flatMap(t => (t?.teams || []));
        }
      } else {
        const teamData = await fetchJson(`${API}/admin/teams?auth_token=${token}`);
        allTeams = teamData?.teams || [];
      }
      const sortedTeams = allTeams.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTeamsApi(sortedTeams);
    };

    load();
  }, []);

  /** Команды из данных таймшита */
  const teamsFromMembers = useMemo(() => {
    if (!data?.members?.length) return [];
    const map = new Map();
    for (const m of data.members) {
      const depId = Number(m.department_id) || null;
      const tId = m.team_id ?? null;
      const tName = m.team_name || '';
      if (!tName) continue;
      const key = `${depId}|${tId ?? 'name:' + tName}`;
      if (!map.has(key)) {
        map.set(key, {
          id: tId ?? null,
          name: tName,
          department_id: depId,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [data?.members]);

  /** Объединяем команды */
  const mergedAllTeams = useMemo(() => {
    const map = new Map();
    const put = (t) => {
      if (!t) return;
      const depId = Number(t.department_id) || null;
      const idPart = (t.id ?? null) !== null ? `id:${t.id}` : `name:${t.name}`;
      const key = `${depId}|${idPart}`;
      if (!map.has(key)) map.set(key, { id: t.id ?? null, name: t.name, department_id: depId });
    };
    (teamsApi || []).forEach(put);
    (teamsFromMembers || []).forEach(put);
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [teamsApi, teamsFromMembers]);

  /** Видимые команды */
  const visibleTeams = useMemo(() => {
    return selectedDepartment?.id
      ? mergedAllTeams.filter(t => Number(t.department_id) === Number(selectedDepartment.id))
      : mergedAllTeams;
  }, [mergedAllTeams, selectedDepartment]);

  /** Ошибка команд */
  useEffect(() => {
    if (selectedDepartment?.id && visibleTeams.length === 0) {
      setTeamsError('No teams available for your departments.');
    } else {
      setTeamsError(null);
    }
  }, [selectedDepartment, visibleTeams.length]);

  /* Overlay highlight */
  const handleHover = useCallback(
    throttle((cellRect, rowRect) => {
      if (!tableWrapRef.current) return;
      const wrap = tableWrapRef.current.getBoundingClientRect();
      if (rowOverlayRef.current && colOverlayRef.current) {
        rowOverlayRef.current.style.top = `${rowRect.top - wrap.top}px`;
        rowOverlayRef.current.style.height = `${rowRect.height}px`;
        rowOverlayRef.current.style.display = 'block';

        colOverlayRef.current.style.left = `${cellRect.left - wrap.left}px`;
        colOverlayRef.current.style.width = `${cellRect.width}px`;
        colOverlayRef.current.style.display = 'block';
      }
    }, 16),
    [],
  );
  useEffect(() => () => handleHover.cancel(), [handleHover]);

  const clearOverlay = useCallback(() => {
    if (rowOverlayRef.current) rowOverlayRef.current.style.display = 'none';
    if (colOverlayRef.current) colOverlayRef.current.style.display = 'none';
  }, []);

  /* Filtering */
  const [reqId, bumpReq] = useReducer(reqReducer, 0);
  const handleApply = useCallback(
    (start, end) => {
      if (end < start) {
        setErr('"To" date cannot be before "From" date');
        return;
      }
      flushSync(() => {
        setFilterStart(start);
        setFilterEnd(end);
        bumpReq();
      });
      fetchData(start, end, reqId + 1, selectedDepartment?.id, selectedTeam?.id);
    },
    [fetchData, setErr, reqId, selectedDepartment, selectedTeam, setFilterStart, setFilterEnd]
  );

  /* Handle date header click */
  const handleDateClick = useCallback((date) => {
    setStatusFilterModal((prev) => ({ open: true, date: date || prev.date }));
  }, []);

  /* Reset filters */
  const resetFilters = useCallback(() => {
    setStatusFilterModal({ open: false, date: null });
    setSelectedStatuses([]);
  }, []);

  /* Sorting function (по данным) */
  const sortMembers = useCallback((members) => {
    const sorted = [...members].sort((a, b) => {
      let aVal, bVal;

      if (sortBy === 'name') {
        aVal = a.employee ? `${a.employee.first_name || ''} ${a.employee.last_name || ''}`.trim() : '';
        bVal = b.employee ? `${b.employee.first_name || ''} ${b.employee.last_name || ''}`.trim() : '';
      } else if (sortBy === 'department') {
        const depA = departments.find(d => Number(d.id) === Number(a.department_id))?.name || a.department_name || '';
        const depB = departments.find(d => Number(d.id) === Number(b.department_id))?.name || b.department_name || '';
        aVal = depA;
        bVal = depB;
      } else {
        aVal = '';
        bVal = '';
      }

      const comparison = (aVal || '').localeCompare(bVal || '', undefined, { sensitivity: 'base' });
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [sortBy, sortOrder, departments]);

  /* Memoized filtered data with status filtering and sorting */
  const filteredData = useMemo(() => {
    if (!data?.members) return { ...data, members: [] };

    const filteredMembers = data.members.filter((m) => {
      if (!m.employee) return false;
      const full = `${m.employee.first_name || ''} ${m.employee.last_name || ''}`.toLowerCase();
      const q = search.toLowerCase();
      const idName = m.employee.id_name ? m.employee.id_name.toString() : '';

      // Apply status filters
      let statusMatch = true;
      if (selectedStatuses.length > 0 && statusFilterModal.date) {
        const inf = cellInfo(m, statusFilterModal.date);
        const iso = format(statusFilterModal.date, 'yyyy-MM-dd');
        const dayObj = m.days.find((day) => day.date === iso) || { records: [] };
        const hasIn = dayObj.records.some((r) => r.event_type === 'clock_in');
        const vac = m.vacations.find((v) => iso >= v.start_date && iso <= v.end_date);

        statusMatch = selectedStatuses.every((status) => {
          if (status === 'vacation') return !!vac;
          if (status === 'no_records') return !hasIn && !vac;
          if (status === 'on_time') return hasIn && !inf.late && !inf.outOfZone && !inf.missedOut;
          return inf[status];
        });
      }

      return (
        (!selectedDepartment || Number(m.department_id) === Number(selectedDepartment.id)) &&
        (!selectedTeam || Number(m.team_id) === Number(selectedTeam.id)) &&
        (!q || full.includes(q) || idName.includes(q)) &&
        statusMatch
      );
    });

    const sortedMembers = sortMembers(filteredMembers);
    return { ...data, members: sortedMembers };
  }, [
    data,
    search,
    selectedDepartment?.id,
    selectedTeam?.id,
    selectedStatuses,
    statusFilterModal.date,
    sortMembers
  ]);

  // Снимаем индикатор сортировки, когда ссылка на members изменилась
  useEffect(() => {
    if (filteredDataRef.current !== filteredData) {
      filteredDataRef.current = filteredData;
      if (isSorting) setIsSorting(false);
    }
  }, [filteredData, isSorting]);

  /* Excel export */
  const exportExcel = useCallback(() => {
    if (!filteredData?.members) return;

    const headerDates = dateRange.map((d) => format(d, 'd'));
    const headerDays = dateRange.map((d) => format(d, 'EE'));
    const aoa = [
      ['ID', 'Name', 'Department/Team', ...headerDates],
      ['', '', '', ...headerDays],
    ];

    filteredData.members.forEach((m) => {
      const dep = departments.find((d) => Number(d.id) === Number(m.department_id))?.name || m.department_name || '';
      const team = m.team_name || '';
      const displayName = dep && team ? `${dep} / ${team}` : dep || team || '—';
      const row = [
        m.employee?.id_name || '',
        m.employee ? `${m.employee.first_name || ''} ${m.employee.last_name || ''}` : '',
        displayName
      ];

      dateRange.forEach((d) => {
        const iso = format(d, 'yyyy-MM-dd');
        const vac = m.vacations.find((v) => iso >= v.start_date && iso <= v.end_date);
        const inf = cellInfo(m, d);
        const dayObj = m.days.find((day) => day.date === iso) || { records: [] };
        const hasIn = dayObj.records.some((r) => r.event_type === 'clock_in');

        let cellValue = '';
        if (vac) {
          cellValue = vac.reason;
          if (hasIn && inf.hrsFormatted) {
            cellValue += ` (${inf.hrsFormatted})`;
          }
        } else if (inf.hrsFormatted) {
          cellValue = inf.hrsFormatted;
        }

        if (hasIn) {
          const statuses = [];
          if (inf.late) statuses.push('⏰');
          if (inf.outOfZone) statuses.push('📍');
          if (inf.missedOut) statuses.push('⚠️');
          if (inf.earlyLeave) statuses.push('❌');
          if (statuses.length > 0) cellValue += ` ${statuses.join(' ')}`;
        }

        row.push(cellValue);
      });
      aoa.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    filteredData.members.forEach((m, rI) => {
      const excelRow = rI + 2;
      dateRange.forEach((d, cI) => {
        const col = 3 + cI;
        const addr = XLSX.utils.encode_cell({ r: excelRow, c: col });
        if (!ws[addr]) ws[addr] = { t: 's', v: aoa[excelRow][col] || '' };

        const iso = format(d, 'yyyy-MM-dd');
        const vac = m.vacations.find((v) => iso >= v.start_date && iso <= v.end_date);
        const inf = cellInfo(m, d);
        const dayObj = m.days.find((day) => day.date === iso) || { records: [] };
        const hasIn = dayObj.records.some((r) => r.event_type === 'clock_in');
        const isSunday = format(d, 'EEEE') === 'Sunday';
        const isTodayVal = isToday(d);

        let fillColor;
        if (vac) fillColor = 'FFBBDEFB';
        else if (isTodayVal) fillColor = 'FFFFF9C4';
        else if (isSunday) fillColor = 'FFE3F2FD';
        else if (!hasIn) fillColor = 'FFFFE6E6';
        else fillColor = 'FFC8E6C9';

        const borderStyle = (inf.late || inf.outOfZone || inf.missedOut || inf.earlyLeave) ? {
          top: { style: 'thin', color: { rgb: 'FFD32F2F' } },
          bottom: { style: 'thin', color: { rgb: 'FFD32F2F' } },
          left: { style: 'thin', color: { rgb: 'FFD32F2F' } },
          right: { style: 'thin', color: { rgb: 'FFD32F2F' } },
        } : vac && hasIn ? {
          top: { style: 'medium', color: { rgb: 'FF4CAF50' } },
          bottom: { style: 'medium', color: { rgb: 'FF4CAF50' } },
          left: { style: 'medium', color: { rgb: 'FF4CAF50' } },
          right: { style: 'medium', color: { rgb: 'FF4CAF50' } },
        } : {};

        ws[addr].s = {
          fill: { fgColor: { rgb: fillColor } },
          font: { color: { rgb: '000000' }, sz: 5 },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: borderStyle,
        };
      });
    });

    for (let c = 0; c < aoa[0].length; c++) {
      const addr1 = XLSX.utils.encode_cell({ r: 0, c });
      const addr2 = XLSX.utils.encode_cell({ r: 1, c });
      ws[addr1] = ws[addr1] || { t: 's', v: aoa[0][c] || '' };
      ws[addr2] = ws[addr2] || { t: 's', v: aoa[1][c] || '' };
      ws[addr1].s = {
        font: { bold: true, sz: 5 },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: 'FFE0E0E0' } },
      };
      ws[addr2].s = {
        font: { sz: 4.5, color: { rgb: 'FF666666' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: 'FFE0E0E0' } },
      };
    }

    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 22 },
      ...dateRange.map(() => ({ wch: 8 })),
    ];
    ws['!freeze'] = { xSplit: 3, ySplit: 2 };

    const legendAoa = [
      ['Legend', ''],
      ['Symbol/Color', 'Description'],
      ['⏰', 'Late clock-in'],
      ['📍', 'Out of zone (unconfirmed)'],
      ['⚠️', 'Missing clock-out'],
      ['❌', 'Early clock-out'],
      ['Green background', 'Clocked in (any status)'],
      ['Red background', 'No records'],
      ['Yellow background', 'Today'],
      ['Light blue background', 'Sunday'],
      ['Blue background', 'Vacation'],
      ['Red border', 'Issue (late, out of zone, or missing clock-out)'],
      ['Green border', 'Vacation with clock-in'],
    ];
    const legendWs = XLSX.utils.aoa_to_sheet(legendAoa);
    legendWs['!cols'] = [{ wch: 15 }, { wch: 30 }];
    for (let r = 0; r < legendAoa.length; r++) {
      for (let c = 0; c < 2; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        legendWs[addr] = legendWs[addr] || { t: 's', v: legendAoa[r][c] || '' };
        legendWs[addr].s = {
          font: { sz: 5, bold: r < 2 },
          alignment: { horizontal: 'left', vertical: 'center' },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');
    XLSX.utils.book_append_sheet(wb, legendWs, 'Legend');
    XLSX.writeFile(wb, `timesheet-${entity_type}-${entity_id}.xlsx`);
  }, [filteredData, dateRange, entity_type, entity_id, departments]);

  /* Cell renderer with tooltips — FIX: vacation colspan doesn’t cross days with clock-in */
  const renderCells = useCallback((member) => {
    const cells = [];
    for (let i = 0; i < dateRange.length; i += 1) {
      const date = dateRange[i];
      const iso = format(date, 'yyyy-MM-dd');
      const vac = member.vacations.find((v) => iso >= v.start_date && iso <= v.end_date);
      const dayObj = member.days.find((d) => d.date === iso) || { records: [] };
      const hasIn = dayObj.records.some((r) => r.event_type === 'clock_in');

      // ✅ merge only consecutive vacation days WITHOUT clock-in
      if (vac && !hasIn) {
        let endIdx = i;
        for (let k = i + 1; k < dateRange.length; k++) {
          const isoK = format(dateRange[k], 'yyyy-MM-dd');
          const stillVacation = isoK >= vac.start_date && isoK <= vac.end_date;
          if (!stillVacation) break;

          const dayObjK = member.days.find((d) => d.date === isoK) || { records: [] };
          const hasInK = dayObjK.records.some((r) => r.event_type === 'clock_in');
          if (hasInK) break; // stop before first vacation day WITH clock-in

          endIdx = k;
        }

        const span = endIdx - i + 1;
        cells.push(
          <Tooltip key={`vac-${i}`} title={generateTooltipText(member, date, {}, vac, false, dayObj)} arrow>
            <TableCell
              colSpan={span}
              align="center"
              className="day-cell"
              sx={{
                backgroundColor: getVacationBackground(vac, false),
                border: getVacationBorder(vac, false),
                whiteSpace: 'nowrap',
                fontSize: '0.48rem',
                padding: '2.4px',
                cursor: 'default',
              }}
              onMouseEnter={(e) => handleHover(
                e.currentTarget.getBoundingClientRect(),
                e.currentTarget.parentElement.getBoundingClientRect(),
              )}
            >
              {vac.reason}
            </TableCell>
          </Tooltip>
        );
        i = endIdx;
        continue;
      }

      const inf = cellInfo(member, date);
      const today = isToday(date);
      const icons = [];

      if (inf.late) icons.push(<AccessTimeFilledIcon key="late" fontSize="inherit" sx={{ color: '#FBC02D' }} />);
      if (inf.outOfZone) icons.push(<PlaceIcon key="zone" fontSize="inherit" sx={{ color: '#D32F2F' }} />);
      if (!today) {
        if (inf.missedOut) icons.push(<ErrorOutlineIcon key="miss" fontSize="inherit" sx={{ color: '#FF5722' }} />);
        if (inf.earlyLeave) icons.push(<LogoutIcon key="early" fontSize="inherit" sx={{ color: '#FF5722' }} />);
        if (!inf.late && !inf.outOfZone && !inf.missedOut && !inf.earlyLeave && inf.hrs) {
          icons.push(<TaskAltIcon key="ok" fontSize="inherit" sx={{ color: '#388E3C' }} />);
        }
      } else {
        if (hasIn && !inf.late && !inf.outOfZone) {
          icons.push(<TaskAltIcon key="ok" fontSize="inherit" sx={{ color: '#388E3C' }} />);
        }
      }

      const cellBg = vac && hasIn ? getVacationBackground(vac, hasIn) : dayBg(inf.hrsFormatted, date);
      const cellBorder = vac && hasIn ? getVacationBorder(vac, hasIn) : '1px solid rgba(224,224,224,0.8)';
      const displayText = vac && hasIn ? `${vac.reason} (${inf.hrsFormatted})` : (vac ? vac.reason : inf.hrsFormatted);

      cells.push(
        <Tooltip
          key={i}
          title={generateTooltipText(member, date, inf, vac, hasIn, dayObj)}
          arrow
        >
          <TableCell
            align="center"
            className="day-cell"
            sx={{
              bgcolor: cellBg,
              border: cellBorder,
              cursor: 'pointer',
              p: '2.4px',
            }}
            onMouseEnter={(e) => handleHover(
              e.currentTarget.getBoundingClientRect(),
              e.currentTarget.parentElement.getBoundingClientRect(),
            )}
            onClick={() => setDetail({
              employee: member.employee,
              day: dayObj,
              zones: member.zones,
              startTime: inf.startTime || '09:30',
            })}
          >
            <Box sx={{ fontSize: '0.48rem', p: 0.16 }}>
              {vac && hasIn ? (
                <>
                  <div>{vac.reason}</div>
                  <div>({inf.hrsFormatted})</div>
                </>
              ) : (
                displayText
              )}
            </Box>
            {icons}
          </TableCell>
        </Tooltip>
      );
    }
    return cells;
  }, [dateRange, handleHover, setDetail]);

  /* Loading / error */
  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 2.4, textAlign: 'center' }}>
          <CircularProgress size={14.4} />
        </Box>
      </Layout>
    );
  }
  if (err) {
    return (
      <Layout>
        <Box sx={{ p: 1.2 }}>
          <Typography color="error" variant="body2" sx={{ fontSize: '0.51rem' }}>{err}</Typography>
          <Button variant="contained" size="small" onClick={() => navigate(-1)} sx={{ mt: 0.6 }}>
            Back
          </Button>
        </Box>
      </Layout>
    );
  }

  /* Render */
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 0.9, maxWidth: '90vw', overflowX: 'auto' }}>
          <Typography variant="h5">{`${entity_type.toUpperCase()} #${entity_id}`}</Typography>
          <Grid container spacing={0.9} alignItems="center">
            <Grid item xs={12} md={8}>
              {teamsError && (
                <Typography color="error" variant="body2" sx={{ mb: 1, fontSize: '0.51rem' }}>
                  {teamsError}
                </Typography>
              )}
              <Filters
                filterStart={filterStart}
                filterEnd={filterEnd}
                setFilterStart={setFilterStart}
                setFilterEnd={setFilterEnd}
                handleApply={handleApply}
                exportExcel={exportExcel}
                navigate={navigate}
                search={search}
                setSearch={setSearch}
                departments={departments}
                selectedDepartment={selectedDepartment}
                setSelectedDepartment={setSelectedDepartment}
                teams={visibleTeams}
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Statistics data={filteredData} dateRange={dateRange} cellInfo={cellInfo} />
            </Grid>
          </Grid>
          <Legend />
          <Box ref={tableWrapRef} sx={{ position: 'relative' }} onMouseLeave={clearOverlay}>
            {/* overlays */}
            <Box
              ref={rowOverlayRef}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 1,
                bgcolor: 'rgba(0,0,0,0.08)',
                display: 'none',
              }}
            />
            <Box
              ref={colOverlayRef}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 1,
                bgcolor: 'rgba(0,0,0,0.08)',
                display: 'none',
              }}
            />

            {/* таблица */}
            <TimesheetTable
              data={filteredData}
              dateRange={dateRange}
              renderCells={renderCells}
              navigate={navigate}
              onHeaderHover={handleHover}
              onDateClick={handleDateClick}
              departments={departments}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              isSorting={isSorting}
            />

            {/* анимация сортировки поверх таблицы */}
            {isSorting && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(2px)',
                  background: 'rgba(255,255,255,0.4)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Sorting…
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <DayDetailDialog
            detail={detail}
            setDetail={setDetail}
            fetchData={fetchData}
            filterStart={filterStart}
            filterEnd={filterEnd}
            reqId={reqId}
            selectedDepartment={selectedDepartment}
            selectedTeam={selectedTeam}
          />
          <StatusFilterModal
            open={statusFilterModal.open}
            date={statusFilterModal.date}
            onClose={() => setStatusFilterModal((prev) => ({ ...prev, open: false }))}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            resetFilters={resetFilters}
          />
        </Box>
      </Layout>
    </ThemeProvider>
  );
}

/* Legend */
const Legend = () => (
  <LegendBox>
    <LegendItemBox>
      <TaskAltIcon fontSize="small" sx={{ color: '#388E3C' }} />
      <Typography variant="caption">On time & in zone</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <AccessTimeFilledIcon fontSize="small" sx={{ color: '#FBC02D' }} />
      <Typography variant="caption">Late clock-in</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <PlaceIcon fontSize="small" sx={{ color: '#D32F2F' }} />
      <Typography variant="caption">Out of zone (unconfirmed)</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <ErrorOutlineIcon fontSize="small" sx={{ color: '#FF5722' }} />
      <Typography variant="caption">Missing clock-out</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <LogoutIcon fontSize="small" sx={{ color: '#FF5722' }} />
      <Typography variant="caption">Early clock-out</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <LegendSwatchBox color="#FFE6E6" />
      <Typography variant="caption">No records</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <LegendSwatchBox color="#FFF9C4" />
      <Typography variant="caption">Today</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <LegendSwatchBox color="#E3F2FD" />
      <Typography variant="caption">Sunday</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <LegendSwatchBox color="#BBDEFB" />
      <Typography variant="caption">Vacation</Typography>
    </LegendItemBox>
    <LegendItemBox>
      <Box sx={{
        width: 9,
        height: 9,
        backgroundColor: '#BBDEFB',
        border: '2px solid #4CAF50',
        borderRadius: 2.25
      }} />
      <Typography variant="caption">Vacation with clock-in</Typography>
    </LegendItemBox>
  </LegendBox>
);
